import "core-js/es6/map";
import "core-js/es6/set";
import "raf/polyfill";
import { v4 as uuid } from "uuid";

import * as __PACKAGE__ from "../package.json";

import React from "react";
import ReactDOM from "react-dom";

import SWGNewsList from "./SWGNews";
import initializeSettings from "./helpers/initializeSettings";

/**
 * The widget is namespaced by default SWG_Widget<br/>
 * To initialize the widget, create a new instance with SWG_Widget.News.<br/>
 * It can be initialized with no arguments (default settings), or with a DOM Element(s) selector and/ or with a config object to provide custom widget configurations.
 *
 * @class
 * @param {...string | Settings} [args]
 * @returns {News}
 *
 * @example
 * <div class="swg-widget--news"></div>
 *
 * <script type="text/javascript" src="https://widgets.sunwingtravelgroup.com/newswidgets/news.latest.js"></script>
 * <script>
 *  var newsWidget = new SWG_Widget.News();
 * </script>
 */
export class News {
  constructor(...args) {
    this.el = "";
    this.config = {};
    this.initialized = false;
    this.init = this.init;
    this.destroy = this.destroy;
    this.update = this.update;

    let el, _selector, _config;

    let DEFAULTS = {
      selector: ".swg-widget--news",
      config: {}
    };

    // Class only accepts one or two arguments, rest is ignored and defaults to deault settings:
    // String = selector
    // Object = config
    if (args.length === 1) {
      if (args[0].constructor && args[0].constructor === Object) {
        // Only config is supplied
        _config = args[0];
        _selector = DEFAULTS.selector;
      } else if (
        args[0].constructor &&
        (args[0].constructor === String ||
          args[0].constructor === HTMLDivElement)
      ) {
        // Only selector is supplied
        _selector = args[0];
        _config = DEFAULTS.config;
      }
    } else if (args.length === 2) {
      if (
        (args[0].constructor && args[0].constructor === String) ||
        args[0].constructor === HTMLDivElement
      ) {
        _selector = args[0];
        if (args[1].constructor && args[1].constructor === Object) {
          _config = args[1];
        }
      } else if (args[0].constructor && args[0].constructor === Object) {
        _config = args[0];
        if (
          (args[1].constructor && args[1].constructor === String) ||
          args[1].constructor === HTMLDivElement
        ) {
          _selector = args[1];
        }
      }
    } else {
      _selector = DEFAULTS.selector;
      _config = DEFAULTS.config;
    }

    // Validate selector exists in DOM
    // If DOM element does not exist, ignore initialization
    if (_selector.constructor === HTMLDivElement) {
      this.el = _selector;
    } else {
      el = window.document.querySelectorAll(_selector);

      if (el && el.length > 0) {
        if (el.length > 1) {
          const news = [];
          for (let index = 0; index < el.length; index++) {
            news.push(new News(el[index], _config));
          }
          return news;
        } else {
          this.el = el[0];
        }
      } else {
        console.warn(
          "SWG Widgets - News - No selector was provided or found. Please define a selector or use the default css class `.swg-widget--news`"
        );
        return false;
      }
    }

    // Set up widget config - Defaults < JS < Data Attributes
    this.config = initializeSettings(this.el, _config);
    this.init();
  }

  /**
   * Generates unique id and renders widget
   * @returns {News}
   */
  init() {
    if (this.initialized) return;

    let widgetId = this.el.id;
    if (widgetId === "") {
      widgetId = `swg-widget--news-${uuid().substring(0, 8)}`;
    }

    this.initialized = true;

    ReactDOM.render(
      <SWGNewsList
        widgetId={widgetId}
        widgetInstance={this.el}
        settings={this.config}
      />,
      this.el
    );

    return this;
  }

  /**
   * Updates Configuration (overwriting data-* attributes), destroys old instance and initializes with new configurations
   * @param {Settings} _config
   * @returns {News}
   */
  update(_config) {
    this.config = Object.assign({}, this.config, _config);
    this.destroy();
    this.init();

    return this;
  }

  /**
   * Destroys and unmounts News Widget (React component)
   * @returns {News}
   */
  destroy() {
    if (!this.initialized) return;

    ReactDOM.unmountComponentAtNode(this.el);

    this.initialized = false;

    return this;
  }
}
